import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * Cart 2.0 — Add a recommended product from inside the cart drawer.
 *
 * Used by cart-product-card (Complete Your Routine + empty-cart rails). Adds the
 * card's variant via /cart/add.js and re-renders through the existing pipeline
 * (CartUpdateEvent → cart-items-component morph). Mirrors the gift-bag/rewards
 * controllers; no product-form machinery needed.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 * @extends {Component<Refs>}
 */
class CartAddButton extends Component {
  /** @type {boolean} */
  #loading = false;

  onAdd() {
    if (this.#loading) return;
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.#loading = true;
    this.classList.add('cart-add-button--loading');

    const sectionIds = new Set();
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    }

    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((parsed) => {
        if (parsed.status || parsed.errors) {
          console.error('[cart-add]', parsed.description || parsed.errors);
          return;
        }
        this.dispatchEvent(
          new CartUpdateEvent(parsed, Array.from(sectionIds)[0] ?? 'cart', {
            source: 'cart-add',
            sections: parsed.sections,
          })
        );
      })
      .catch((error) => console.error('[cart-add]', error))
      .finally(() => {
        this.#loading = false;
        this.classList.remove('cart-add-button--loading');
      });
  }
}

if (!customElements.get('cart-add-button')) {
  customElements.define('cart-add-button', CartAddButton);
}
